import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    FormControl,
    TextField,
    Switch,
    Chip,
    useTheme,
    FormControlLabel
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications'; // Example Icon
import { useAuth } from '../../hooks/useAuth';

import { getNotificationSettings, setNotificationSettings } from "../../hooks/apiHandler";


// --- Main Component ---
const NotificationSettings = ({ storeid }) => { // Pass storeid as a prop
    const theme = useTheme();
    const { Logout } = useAuth();
    const storeID = useRef(JSON.parse(window.localStorage.getItem("ds")));

    const [settings, setSettings] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [loading, setLoading] = useState(true);
    const [emailInput, setEmailInput] = useState(''); // Temp state for adding emails

    // Fetch initial settings
    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const response = await getNotificationSettings({ storeID: storeID.current }, Logout);
                if (response?.code === 0 && response.data) {
                    // Ensure defaults if fields are missing from DB
                    const data = response.data;
                    data.notifications = data.notifications || {};
                    data.notifications.dailyNotification = data.notifications.dailyNotification || { isEnabled: true, preferdTime: '09:00' };
                    data.notifications.weeklyNotification = data.notifications.weeklyNotification || { isEnabled: false };
                    data.notifications.monthlyNotification = data.notifications.monthlyNotification || { isEnabled: false };
                    setSettings(data);
                } else {
                    setSettings({
                        storeID: storeid,
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        email: [],
                        notifications: {
                            dailyNotification: { isEnabled: true, preferdTime: '09:00' },
                            weeklyNotification: { isEnabled: false },
                            monthlyNotification: { isEnabled: false }
                        }
                    });
                    console.warn("No settings found or API error:", response?.msg);
                }
            } catch (error) {
                console.error("Error fetching notification settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [storeid, Logout]);

    // --- Handlers ---
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        // Handle nested notification settings
        if (name.includes('.')) {
            const keys = name.split('.'); // e.g., ['notifications', 'dailyNotification', 'preferdTime']
            setSettings(prev => {
                let currentLevel = prev;
                // Navigate down the object structure
                for (let i = 0; i < keys.length - 1; i++) {
                    currentLevel = currentLevel[keys[i]];
                }
                // Update the final property
                currentLevel[keys[keys.length - 1]] = value;
                return { ...prev }; // Return a new object to trigger re-render
            });
        } else {
            // Handle top-level settings like timeZone
            setSettings(prev => ({ ...prev, [name]: value }));
        }
    };


    const handleSwitchChange = (event) => {
        const { name, checked } = event.target;
        const keys = name.split('.'); // e.g., ['notifications', 'dailyNotification', 'isEnabled']
        setSettings(prev => {
            let current = prev;
            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }
            current[keys[keys.length - 1]] = checked;
            return { ...prev };
        });
    };

    const handleAddEmail = () => {
        if (emailInput && !settings.email.includes(emailInput) && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput)) {
            setSettings(prev => ({ ...prev, email: [...prev.email, emailInput.trim()] }));
            setEmailInput('');
        } else {
            console.warn("Invalid or duplicate email");
        }
    };

    const handleDeleteEmail = (emailToDelete) => {
        setSettings(prev => ({ ...prev, email: prev.email.filter(email => email !== emailToDelete) }));
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Send only the fields relevant to this schema
            const updatesToSend = {
                timeZone: settings.timeZone,
                email: settings.email,
                notifications: settings.notifications
            };
            const resp = await setNotificationSettings({ storeID: storeID.current, updates: updatesToSend }, Logout);
            if (resp.code === 0) {
                setSettings(resp.data);
                console.log("Save successful!");
            } else {
                 console.error("Save failed:", resp.msg);
            }
        } catch (error) {
            console.error('Save error:', error);
        } finally {
            setLoading(false);
            setEditMode(false);
        }
    };

     const handleCancel = async () => {
        setEditMode(false);
        setLoading(true);
         try {
             const response = await getNotificationSettings({ storeID: storeid }, Logout);
             if (response?.code === 0 && response.data) setSettings(response.data);
         } catch(error) { console.error("Error refetching settings:", error); }
         finally { setLoading(false); }
    };

    if (loading || !settings) {
        return <Typography>Loading Settings...</Typography>;
    }

    return (
        <Box sx={{ width: { md: '70%', xs: '100%' } }}>
            {/* --- Header Section --- */}
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={12} md={6}>
                    <h4>Notification Settings</h4>
                </Grid>
                <Grid item xs={12} md="auto" sx={{ mt: { xs: 2, md: 0 } }}>
                    {editMode && <Button onClick={handleCancel} sx={{ mr: 1 }}>Cancel</Button>}
                    {!editMode ? (
                        <Button variant="outlined" onClick={() => setEditMode(true)}>Edit</Button>
                    ) : (
                        <Button variant="contained" onClick={handleSave}>Save</Button>
                    )}
                </Grid>
            </Grid>
            <hr />

            {/* --- General Settings --- */}
            <Grid container alignItems="center" spacing={2} columns={4}>
                <Grid item xs={12} md={1}> <Typography>Time Zone:</Typography> </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl sx={{ m: 1, width: { xs: '100%', md: '60ch' } }} variant="filled">
                        <TextField variant="filled" name="timeZone" value={settings.timeZone || ''} onChange={handleInputChange}
                            placeholder="e.g., America/Edmonton" size="small" disabled={!editMode} />
                    </FormControl>
                </Grid>
            </Grid>

            {/* --- Email Recipients --- */}
            <h4>Email Recipients</h4> <hr />
            <Grid container alignItems="flex-start" spacing={2} columns={4}>
                <Grid item xs={12} md={1}> <Typography sx={{ pt: 2 }}>Emails:</Typography> </Grid>
                <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, m: 1, mb: editMode ? 1 : 2 }}>
                        {settings.email.map((email) => (
                            <Chip key={email} label={email} onDelete={editMode ? () => handleDeleteEmail(email) : undefined} disabled={!editMode}/>
                        ))}
                        {settings.email.length === 0 && !editMode && <Typography variant="caption" color="textSecondary">(No recipients)</Typography>}
                    </Box>
                    {editMode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', m: 1 }}>
                            <FormControl sx={{ flexGrow: 1, mr: 1, width: { xs: 'calc(100% - 100px)', md: 'calc(60ch - 100px)' } }} variant="filled">
                                <TextField variant="filled" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                                    placeholder="Add new email" type="email" size="small" onKeyDown={(e) => { if (e.key === 'Enter') handleAddEmail(); }}/>
                            </FormControl>
                            <Button variant="outlined" onClick={handleAddEmail} size="medium"> Add </Button>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* --- Notification Types --- */}
            <h4>Report Notifications</h4> <hr />
            <Grid container alignItems="center" spacing={2} columns={4}>
                {/* Daily Report */}
                <Grid item xs={12} md={1}> <Typography>Daily Report:</Typography> </Grid>
                <Grid item xs={12} md={3} sx={{ pl: { md: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                         <FormControlLabel control={ <Switch name="notifications.dailyNotification.isEnabled" checked={settings.notifications.dailyNotification.isEnabled}
                            onChange={handleSwitchChange} disabled={!editMode}/> }
                            label={settings.notifications.dailyNotification.isEnabled ? "Enabled" : "Disabled"} labelPlacement="end" />

                        {/* ✅ NEW: Time Picker for Daily Report */}
                         {settings.notifications.dailyNotification.isEnabled && (
                             <FormControl sx={{ width: '15ch' }} variant="filled">
                                 <TextField label="Send Time" variant="filled" name="notifications.dailyNotification.preferdTime"
                                    type="time" value={settings.notifications.dailyNotification.preferdTime || '09:00'}
                                    onChange={handleInputChange} size="small" disabled={!editMode}
                                    InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} // step="300" (5 minutes)
                                 />
                             </FormControl>
                         )}
                    </Box>
                </Grid>

                {/* Weekly Report */}
                <Grid item xs={12} md={1}> <Typography>Weekly Report:</Typography> </Grid>
                 <Grid item xs={12} md={3} sx={{ pl: { md: 2 } }}>
                    <FormControlLabel control={ <Switch name="notifications.weeklyNotification.isEnabled" checked={settings.notifications.weeklyNotification.isEnabled}
                        onChange={handleSwitchChange} disabled={!editMode} /> }
                        label={settings.notifications.weeklyNotification.isEnabled ? "Enabled" : "Disabled"} labelPlacement="end"/>
                </Grid>

                {/* Monthly Report */}
                <Grid item xs={12} md={1}> <Typography>Monthly Report:</Typography> </Grid>
                 <Grid item xs={12} md={3} sx={{ pl: { md: 2 } }}>
                     <FormControlLabel control={ <Switch name="notifications.monthlyNotification.isEnabled" checked={settings.notifications.monthlyNotification.isEnabled}
                        onChange={handleSwitchChange} disabled={!editMode} /> }
                        label={settings.notifications.monthlyNotification.isEnabled ? "Enabled" : "Disabled"} labelPlacement="end"/>
                </Grid>
            </Grid>
        </Box>
    );
};

export default NotificationSettings;